// src/pages/PersonnelListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { getAllPersonnel } from '../services/personnelService';
import { getAllCommands } from '../services/commandService';
import { getAllExercises } from '../services/exerciseService';
import { useAuth } from '../context/AuthContext';
import UploadPersonnelDialog from '../components/UploadPersonnelDialog';
import ViewPersonnelDialog from '../components/ViewPersonnelDialog';
import CreatePersonnelDialog from '../components/CreatePersonnelDialog';
import EditPersonnelDialog from '../components/EditPersonnelDialog';

import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';

const PersonnelListPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [personnel, setPersonnel] = useState([]);
  const [commands, setCommands] = useState([]);
  const [activeExercise, setActiveExercise] = useState(null);
  const [activeCommands, setActiveCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [filters, setFilters] = useState({
    commandId: '',
    zone: '',
    verificationStatus: '',
    attendanceStatus: '',
    search: '',
  });

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);

  // FIX: no setLoading(true) on refresh — avoids unmount/page reset
  const fetchPersonnel = useCallback(async () => {
    setError('');
    try {
      const response = await getAllPersonnel(filters);
      setPersonnel(response.data.personnel);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load personnel');
    }
  }, [filters]);

  const fetchCommands = async () => {
    try {
      const response = await getAllCommands();
      setCommands(response.data.commands);
    } catch (err) {
      console.error('Failed to load commands:', err);
    }
  };

  // Determine the active exercise and load only its commands (for Add Personnel)
  const fetchActiveExerciseCommands = async () => {
    try {
      const exRes = await getAllExercises();
      const active = (exRes.data.exercises || []).find((e) => e.status === 'ACTIVE') || null;
      setActiveExercise(active);
      if (active) {
        const cmdRes = await getAllCommands({ exerciseId: active.id });
        setActiveCommands(cmdRes.data.commands || []);
      } else {
        setActiveCommands([]);
      }
    } catch (err) {
      console.error('Failed to load active exercise commands:', err);
    }
  };

  const [initialLoad, setInitialLoad] = useState(true);
  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        const params = Object.assign({}, filters);
        if (activeExercise && activeExercise.id) {
          params.exerciseId = activeExercise.id;
        }
        const response = await getAllPersonnel(params);
        setPersonnel(response.data.personnel);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load personnel');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    if (initialLoad) {
      load();
      fetchCommands();
      fetchActiveExerciseCommands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };
  const handleApplyFilters = () => { fetchPersonnel(); };
  const handleClearFilters = () => {
    setFilters({ commandId: '', zone: '', verificationStatus: '', attendanceStatus: '', search: '' });
  };

  const handleViewPersonnel = (person) => {
    setSelectedPersonnel(person);
    setViewDialogOpen(true);
  };
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPersonnel(null);
  };

  const handleEditPersonnel = (person) => {
    setSelectedPersonnel(person);
    setEditDialogOpen(true);
  };
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedPersonnel(null);
  };

  const getVerificationStatusColor = (status) =>
    status === 'VERIFIED' ? 'success' : 'default';
  const getAttendanceStatusColor = (status) =>
    status === 'PRESENT' ? 'success' : 'default';

  const columns = [
    { id: 'serviceNumber', label: 'Service No.', render: (row) => <strong>{row.serviceNumber}</strong> },
    { id: 'ippisNumber', label: 'IPPIS No.' },
    { id: 'name', label: 'Name', render: (row) => `${row.firstName} ${row.middleName || ''} ${row.lastName}`.trim() },
    { id: 'rankName', label: 'Rank' },
    { id: 'command', label: 'Command', render: (row) => row.command?.code || '-' },
    {
      id: 'preClearance', label: 'Pre-Cleared',
      render: (row) => row.preClearance
        ? <Chip label="Yes" color="success" size="small" />
        : <Chip label="No" color="default" size="small" />,
    },
    {
      id: 'verificationStatus', label: 'Verification',
      render: (row) => <Chip label={row.verificationStatus.replace('_', ' ')} color={getVerificationStatusColor(row.verificationStatus)} size="small" />,
    },
    {
      id: 'attendanceStatus', label: 'Attendance',
      render: (row) => <Chip label={row.attendanceStatus.replace('_', ' ')} color={getAttendanceStatusColor(row.attendanceStatus)} size="small" />,
    },
  ];

  const rowActions = (row) => [
    { icon: <ViewIcon fontSize="small" />, label: 'View Details', onClick: () => handleViewPersonnel(row) },
    ...(isAdmin
      ? [{ icon: <EditIcon fontSize="small" />, label: 'Edit Personnel', onClick: () => handleEditPersonnel(row) }]
      : []),
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <PageHeader
          title="Personnel Management"
          onBack={() => navigate('/')}
          actions={[
            { label: 'Refresh', icon: <RefreshIcon />, onClick: fetchPersonnel, variant: 'outlined' },
            ...(isAdmin ? [
              { label: 'Add Personnel', icon: <PersonAddIcon />, onClick: () => setCreateDialogOpen(true), variant: 'outlined' },
              { label: 'Upload CSV', icon: <UploadIcon />, onClick: () => setUploadDialogOpen(true), variant: 'contained' },
            ] : []),
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField label="Search" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Service No, IPPIS, Name" sx={{ minWidth: 250 }} size="small" />
            <TextField select label="Command" name="commandId" value={filters.commandId} onChange={handleFilterChange} sx={{ minWidth: 250 }} size="small">
              <MenuItem value="">All Commands</MenuItem>
              {commands.map((command) => (
                <MenuItem key={command.id} value={command.id}>{command.code} - {command.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Zone" name="zone" value={filters.zone} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Zones</MenuItem>
              <MenuItem value="ZONE A - LAGOS">Zone A - Lagos</MenuItem>
              <MenuItem value="ZONE B - KADUNA">Zone B - Kaduna</MenuItem>
              <MenuItem value="ZONE B2 - KADUNA">Zone B2 - Kaduna</MenuItem>
              <MenuItem value="ZONE C - BAUCHI">Zone C - Bauchi</MenuItem>
              <MenuItem value="ZONE D - MINNA">Zone D - Minna</MenuItem>
              <MenuItem value="ZONE E - OWERRI">Zone E - Owerri</MenuItem>
              <MenuItem value="ZONE F - IBADAN">Zone F - Ibadan</MenuItem>
              <MenuItem value="ZONE G - BENIN">Zone G - Benin</MenuItem>
              <MenuItem value="ZONE H - BENUE">Zone H - Benue</MenuItem>
              <MenuItem value="SERVICE HEADQUARTERS">Service HQ</MenuItem>
            </TextField>
            <TextField select label="Verification Status" name="verificationStatus" value={filters.verificationStatus} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="NOT_VERIFIED">Not Verified</MenuItem>
              <MenuItem value="VERIFIED">Verified</MenuItem>
            </TextField>
            <TextField select label="Attendance" name="attendanceStatus" value={filters.attendanceStatus} onChange={handleFilterChange} sx={{ minWidth: 150 }} size="small">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PRESENT">Present</MenuItem>
              <MenuItem value="DID_NOT_APPEAR">Did Not Appear</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleApplyFilters} size="small">Apply Filters</Button>
            <Button variant="outlined" onClick={handleClearFilters} size="small">Clear Filters</Button>
          </Box>
        </Paper>

        <DataTable
          columns={columns}
          data={personnel}
          loading={loading}
          rowActions={rowActions}
          emptyType="personnel"
          emptyMessage="No personnel found. Add personnel or upload a CSV, or adjust your filters."
        />

        {!loading && personnel.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Total personnel: <strong>{personnel.length}</strong>
            </Box>
          </Box>
        )}
      </Box>

      {/* Add Personnel Dialog */}
      {isAdmin && (
        <CreatePersonnelDialog
          open={createDialogOpen}
          commands={activeCommands}
          activeExerciseName={activeExercise?.name}
          onNotify={notify}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={() => {
            setCreateDialogOpen(false);
            fetchPersonnel();
          }}
        />
      )}

      {/* Upload Dialog */}
      {isAdmin && (
        <UploadPersonnelDialog
          open={uploadDialogOpen}
          commands={activeCommands}
          onClose={() => setUploadDialogOpen(false)}
          onSuccess={() => {
            setUploadDialogOpen(false);
            fetchPersonnel();
          }}
        />
      )}

      {/* Edit Personnel Dialog */}
      {isAdmin && (
        <EditPersonnelDialog
          open={editDialogOpen}
          personnel={selectedPersonnel}
          onNotify={notify}
          onClose={handleCloseEditDialog}
          onSuccess={() => {
            handleCloseEditDialog();
            fetchPersonnel();
          }}
        />
      )}

      {/* View Personnel Dialog */}
      <ViewPersonnelDialog
        open={viewDialogOpen}
        personnel={selectedPersonnel}
        onClose={handleCloseViewDialog}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PersonnelListPage;